<template>
  <div class="clinicCouponModal">
    <el-form ref="form" :model="editClinicCoupon" :rules="clinicCouponRules" label-width="140px">
      <div v-if="status()" class="text-red text-center mb-3">
        <span>1度でも消化した回数券は有効期限以外を編集できません。</span>
      </div>
      <el-form-item label="店舗" prop="clinic" v-if="permission.can('write_all', 'coupon')">
        <v-select
            :disabled="clinicCouponIsEdit"
            id="currentClinicId"
            v-model="editClinicCoupon.clinic"
            label="clinic_name"
            item-text="clinic_name"
            item-value="id"
            :options="currentClinics"
            filterable
            placeholder="店舗選択"
            required
            @input="onChangeClinic($event)">
          <template #no-options="{ search, searching, loading }">
            該当結果が見つかりません。
          </template>
          <template #open-indicator="{ attributes }">
            <span v-bind="attributes" class="el-select__caret el-input__icon el-icon-arrow-down"></span>
          </template>
        </v-select>
      </el-form-item>
      <el-form-item label="名前" prop="clinic_customer_note">
        <v-select
          :disabled="isDisabled()"
          id="customerNoteId"
          v-model="editClinicCoupon.clinic_customer_note"
          label="all_name_with_uid"
          item-text="all_name_with_uid"
          item-value="id"
          @search="filterCustomers($event)"
          :options="clinicCustomerNotes"
          :filterable="false"
          placeholder="名前を入力し検索"
          required>
          <template #no-options="{ search, searching, loading }">
            該当結果が見つかりません。
          </template>
          <template #open-indicator="{ attributes }">
            <span v-bind="attributes" class="el-select__caret el-input__icon el-icon-arrow-down"></span>
          </template>
        </v-select>
      </el-form-item>
      <el-form-item label="購入日" prop="purchase_date" style="width: 100%">
        <el-date-picker
          :disabled="isDisabled()"
          v-model="editClinicCoupon.purchase_date"
          type="date"
          placeholder="日を選ぶ"
          :picker-options="{ firstDayOfWeek: startWeekday }"
          @change="fillExpiryDate">
        </el-date-picker>
      </el-form-item>
      <el-form-item label="回数券名" prop="menu_coupon_id">
        <el-input v-if="isDisabled()" :disabled="true" :value="editClinicCoupon.menu_coupon_name"/>
        <el-select v-else v-model="editClinicCoupon.menu_coupon_id" filterable placeholder="回数券名" required @change="fillMenuCoupon">
          <el-option
              v-for="menu_coupon in clinicMenuCoupons"
              :key="menu_coupon.id"
              :label="menu_coupon.name"
              :value="menu_coupon.id">
          </el-option>
        </el-select>
        <p v-if="editClinicCoupon.menu_coupon_id == null || editClinicCoupon.menu_coupon_id === ''" style="color: red">設定ボタンから回数券情報の登録をお願いします</p>
      </el-form-item>
      <el-form-item label="担当者" prop="therapist_ids">
        <el-input
          v-if="isDisabled()" :disabled="true"
          type="textarea" autosize
          placeholder="担当者"
          :value="editClinicCoupon.therapists.map(therapist => therapist.name).join(', ')"
        />
        <el-select
          v-else
          v-model="editClinicCoupon.therapist_ids"
          filterable multiple collapse-tags
          placeholder="担当者" @change="onChangeTherapist">
          <el-option :label="'誰でも対応可能'" :value="'select-all'"></el-option>
          <el-option
              v-for="therapist in therapists"
              :key="therapist.id"
              :label="therapist.name"
              :value="therapist.id">
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="提供回数(回)" prop="total_uses">
        <el-input-number
          controls-position="right"
          :min="1"
          v-model="editClinicCoupon.total_uses"
          placeholder="提供回数"
          :disabled="isDisabled() || !!editClinicCoupon.menu_coupon_manager_id"
          :max="2000000000"
          required></el-input-number>
      </el-form-item>
      <el-form-item label="提供時間(分)">
        <el-input-number controls-position="right" :min="null" :max="2000000000" :disabled="isDisabled() || !!editClinicCoupon.menu_coupon_manager_id"
          v-model="editClinicCoupon.duration" :placeholder="durationPlaceholder"></el-input-number>
      </el-form-item>
      <el-form-item label="提供価格(円)" prop="input_price">
        <el-input-number
          controls-position="right" :min="0" :max="9000000000000000000"
          prefix-icon="el-icon-search"
          :disabled="isDisabled() || !!editClinicCoupon.menu_coupon_manager_id"
          v-model="editClinicCoupon.input_price" placeholder="提供価格" required
          @change="updateTaxIncludedDescription"></el-input-number>
      </el-form-item>
      <el-form-item label="提供価格（税込）" v-if="isShowTaxIncludedDescription">
        <p>
          <span>{{ taxPriceIncluded }}</span>
        </p>
      </el-form-item>
      <el-form-item label="税区分" prop="tax_included">
        <el-select v-model="editClinicCoupon.tax_included" placeholder="税区分" @change="updatePriceIncludedTax" :disabled="!!editClinicCoupon.menu_coupon_manager_id || isDisabled()">
          <el-option
            v-for="item in taxTypeOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value">
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="税率" prop="tax_percent">
        <el-select v-model="editClinicCoupon.tax_percent" placeholder="税率" @change="updatePriceIncludedTax" :disabled="isDisabled() || !!editClinicCoupon.menu_coupon_manager_id">
          <el-option
            v-for="item in taxPercentOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value">
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="期限" prop="time_limit_flag">
        <el-checkbox v-model="editClinicCoupon.time_limit_flag" @change="handleExpiryDateRule" :disabled="notWritePermission() || !!editClinicCoupon.menu_coupon_manager_id"></el-checkbox>
      </el-form-item>
      <el-form-item v-if="editClinicCoupon.time_limit_flag" label="有効期限" prop="expiry_date">
        <el-date-picker
          v-model="editClinicCoupon.expiry_date"
          type="date"
          placeholder="日を選ぶ"
          :picker-options="{ firstDayOfWeek: startWeekday }"
          :disabled="notWritePermission() || !!editClinicCoupon.menu_coupon_manager_id">
        </el-date-picker>
      </el-form-item>
      <div class="text-center">
        <el-button type="info" class="btn-clinic-coupon mx-1" @click="SET_CLINIC_COUPON_VISIBLE(false)">閉じる</el-button>
        <el-popconfirm v-if="clinicCouponIsEdit"
          title="回数券を削除します。よろしいでしょうか？"
          confirm-button-text="はい"
          cancel-button-text="キャンセル"
          @confirm="onConfirm(editClinicCoupon)"
        >
          <el-button class="btn-clinic-coupon mx-1" slot="reference" type="danger" v-if="!isDisabled()" >
            削除
          </el-button>
        </el-popconfirm>
        <el-button type="primary" class="btn-clinic-coupon mx-1" @click="submitForm('form')" v-if="!notWritePermission()">{{
          getBtnTitle
        }}</el-button>
      </div>
    </el-form>
  </div>
</template>
<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import permissionService from "../../../services/permission_service";

export default {
  data() {
    return {
      permission: permissionService,
      isShowTaxIncludedDescription: false,
      taxPriceIncluded: "",
      taxPercentOptions: [
        { label: '10%', value: 10 },
        { label: '8%', value: 8 },
        { label: '非課税', value: 0 },
      ],
      taxTypeOptions: [
        { label: '内税', value: true },
        { label: '外税', value: false },
      ],
    }

    this.setIsMenuCouponEmpty()
  },
  updated() {
    this.updateTaxIncludedDescription();
  },
  mounted() {
    this.updateTaxIncludedDescription();
  },
  computed: {
    ...mapGetters(['startWeekday', 'editClinicCoupon', 'clinicCouponRules', 'total', 'clinicCouponIsEdit', 'clinicCustomerNotes', 'therapists', 'dropdownMenuCoupons', 'currentClinics', 'currentClinicCustomerNotes', 'clinicMenuCoupons', 'currentClinic']),
    getBtnTitle() {
      return '登録'
    },
    durationPlaceholder() {
      const selectedMenuCoupon = this.findSelectedMenuCoupon();
      return (this.isDisabled() || (selectedMenuCoupon && selectedMenuCoupon.menu_coupon_manager_id) || !!this.editClinicCoupon.menu_coupon_manager_id) ? '' : '提供時間';
    }
  },
  methods: {
    ...mapActions('clinic_coupon', ['createClinicCoupon', 'updateClinicCoupon', 'removeClinicCoupon']),
    ...mapActions('clinic_customer_note', ['fetchClinicCustomerNotes']),
    ...mapActions('therapist', ['fetchTherapists']),
    ...mapActions('menu_coupon', ['fetchClinicMenuCoupons']),
    ...mapActions('clinic', ['fetchCurrentClinics']),
    ...mapMutations('clinic_coupon', ['SET_CLINIC_COUPON_VISIBLE', 'SET_EXPIRY_DATE_RULE']),
    handleExpiryDateRule(val) {
      if (val) {
        this.editClinicCoupon.time_limit_flag = true
        this.SET_EXPIRY_DATE_RULE(true);
      } else {
        this.editClinicCoupon.time_limit_flag = false
        this.SET_EXPIRY_DATE_RULE(false);
      }
    },
    onConfirm(val){
      this.removeClinicCoupon(val.id)
    },
    filterCustomers(query) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(async () => {
        let clinic_id
        if (this.editClinicCoupon.clinic === undefined) clinic_id = this.currentClinic.id
        else clinic_id = this.editClinicCoupon.clinic.id

        await this.fetchClinicCustomerNotes({query: query, clinic_id: clinic_id})
      }, 300);
    },
    submitForm(formName) {
      this.$refs[formName].validate(async(valid) => {
        if (valid) {
          if (this.clinicCouponIsEdit) {
            this.updateClinicCoupon(this.editClinicCoupon)
          } else {
            this.createClinicCoupon(this.editClinicCoupon)
          }
        } else {
          return false
        }
      })
    },
    updatePriceIncludedTax(value) {
      const currentClinicCoupon = this.editClinicCoupon;
      if (currentClinicCoupon.tax_included) {
        this.taxPriceIncluded = `${currentClinicCoupon.input_price.toLocaleString()}円`;
        this.editClinicCoupon.price = currentClinicCoupon.input_price;
      } else {
        const tax = currentClinicCoupon.input_price * (currentClinicCoupon.tax_percent / 100);
        const totalPrice = Math.floor(currentClinicCoupon.input_price + tax);
        this.taxPriceIncluded = `${totalPrice.toLocaleString()}円`;
        this.editClinicCoupon.price = totalPrice;
      }
    },
    updateTaxIncludedDescription() {
      if (this.editClinicCoupon.input_price >= 0) {
        this.isShowTaxIncludedDescription = true;
        this.updatePriceIncludedTax();
      } else {
        this.isShowTaxIncludedDescription = false;
      }
    },
    status: function disableFieldsCondition() {
      return this.editClinicCoupon.used_times > 0;
    },
    isDisabled() {
      if (this.editClinicCoupon.payment_id) return true;

      return this.notWritePermission() || (this.clinicCouponIsEdit && this.status());
    },
    notWritePermission() {
      if (!this.clinicCouponIsEdit)
        return false;

      let not_write_all = this.editClinicCoupon.clinic.id != this.currentClinic.id && !this.permission.can('write_all', 'coupon');
      return !this.permission.can('write', 'coupon') || not_write_all;
    },
    fillMenuCoupon() {
      const selectedMenuCoupon = this.findSelectedMenuCoupon();
      if (!selectedMenuCoupon) return;

      this.editClinicCoupon.total_uses = selectedMenuCoupon.total_uses
      this.editClinicCoupon.duration = selectedMenuCoupon.duration
      this.editClinicCoupon.input_price = selectedMenuCoupon.input_price
      this.editClinicCoupon.time_limit_flag = selectedMenuCoupon.time_limit_flag
      this.editClinicCoupon.therapist_ids = selectedMenuCoupon.therapist_ids
      this.editClinicCoupon.menu_coupon_manager_id = selectedMenuCoupon.menu_coupon_manager_id

      if (this.editClinicCoupon.purchase_date) {
        this.editClinicCoupon.expiry_date = moment(this.editClinicCoupon.purchase_date, 'YYYY/MM/DD').add(selectedMenuCoupon.number_month_expired, 'M')
      }

      this.editClinicCoupon.tax_included = selectedMenuCoupon.tax_included;
      this.editClinicCoupon.tax_percent = selectedMenuCoupon.tax_percent;
    },
    onChangeTherapist(event){
      if(this.editClinicCoupon.therapist_ids.length === this.therapists.length + 1 ) {
        event.forEach(x => x === 'select-all' ? this.editClinicCoupon.therapist_ids = [] : '')
      }
      else {
        event.forEach(x => x === 'select-all' ? this.editClinicCoupon.therapist_ids = this.therapists.map( x => x.id )  : '')
      }
    },
    translateCouponCalculatorMethod(value){
      return value === 'allow_total' ? '初回利用時に回数券金額を全額計上する' : '回数券金額を利用回数で分配し計上する'
    },

    findSelectedMenuCoupon() {
      return this.clinicMenuCoupons.find((item) => item.id === this.editClinicCoupon.menu_coupon_id)
    },

    async onChangeClinic(event) {
      await this.fetchClinicCustomerNotes({clinic_id: event.id})
      this.fetchTherapists(event.id)
      this.fetchClinicMenuCoupons(event.id)
      this.editClinicCoupon.clinic_customer_note = null
      this.editClinicCoupon.menu_coupon_id = null
      this.editClinicCoupon.therapist_ids = []
    },

    fillExpiryDate() {
      const selectedMenuCoupon = this.findSelectedMenuCoupon()
      if (this.editClinicCoupon.purchase_date && selectedMenuCoupon) {
        this.editClinicCoupon.expiry_date = moment(this.editClinicCoupon.purchase_date, 'YYYY/MM/DD').add(selectedMenuCoupon.number_month_expired, 'M')
      }
    },

    setIsMenuCouponEmpty() {
      this.isMenuCouponEmpty = (this.dropdownMenuCoupons.length === 0 || this.dropdownMenuCoupons.every((item) => item.is_only_name))
    },
  }
}
</script>
<style scoped>
.btn-clinic-coupon {
  width: 85px;
}
</style>
